import { useState } from 'react';
import {
  Box, Typography, Paper, Button, Divider, Alert, Tooltip, Chip, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Block as CancelIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import formatRut from '../formatRut';
import { formatDateTime } from '../formatDate';
import { saveBlob, blobErrorBody } from '../downloadFile';

// Estados de la firma (backend: services/erpContractSignature.service.js)
const ACTIVE_STATUSES = ['sending', 'sent', 'signed_by_worker', 'completed'];
const OPEN_STATUSES = ['sending', 'sent', 'signed_by_worker'];
// Envío en curso (backend: SENDING_GRACE_MS): todavía no se consulta ni se anula
const SENDING_GRACE_MS = 3 * 60 * 1000;
const millisOf = (ts) => {
  const seconds = ts?._seconds ?? ts?.seconds;
  return seconds ? seconds * 1000 : 0;
};
// FECHA_SUSCRIPCION cambia cada día: no cuenta como dato cambiado
const IGNORED_CHANGES = ['FECHA_SUSCRIPCION'];
const STATUS_COLORS = {
  sending: 'info',
  sent: 'warning',
  signed_by_worker: 'info',
  completed: 'success',
  cancelled: 'default',
  failed: 'error',
};

/**
 * Firma del contrato con Izydocs (PLAN_ERP.md §15.4, fase B2, 2026-10-20):
 * enviar el contrato tipo de Izydocs (EST: 222 o 428 según la nacionalidad)
 * con los datos de la ficha, la cotización y el contrato; actualizar el estado
 * con botón (Izydocs no avisa), anular y descargar el PDF firmado. El detalle
 * trae el chequeo (signatureCheck): formato, tags que se van a mandar, lo que
 * falta y el estado de la conexión con Izydocs.
 */
const ErpContractSignatureCard = ({ contract, employeeRecord, signatureCheck, onChanged }) => {
  const { t } = useTranslation();
  const { organization, authToken, showAlert, user } = useStore.getState();
  const [working, setWorking] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const k = (key) => `erp.contracts.signature.${key}`;
  const headers = { Authorization: `Bearer ${authToken}` };

  const signature = contract.signature || null;
  const status = signature?.status || null;
  const check = signatureCheck;
  const provider = check?.provider || null;
  const canSend = !status || !ACTIVE_STATUSES.includes(status);
  const canRefresh = Boolean(status) && (OPEN_STATUSES.includes(status) || (status === 'completed' && !signature.signedFile));
  const canCancel = Boolean(status) && OPEN_STATUSES.includes(status);
  const inProgress = status === 'sending' && Date.now() - millisOf(signature.sentAt) < SENDING_GRACE_MS;

  // Mientras falten los datos del perfil en Izydocs ('profile'), la firma es solo
  // para super-admin, en modo prueba; después aparece para todos (2026-10-22).
  // Sin chequeo (error al cargarlo) se muestra con su aviso, como antes.
  const isSuperAdmin = user?.rol === 'super-admin';
  const released = !(provider?.pending || []).includes('profile');
  // Modo prueba: cuando, por el lado de Izydocs, lo único pendiente son los datos del
  // perfil. Se muestra aunque falten otros datos: activado, el aviso dice cuáles (2026-10-29)
  const sendPending = (provider?.pending || []).filter((op) => ['enrollment', 'profile'].includes(op));
  const showTestSwitch = isSuperAdmin && canSend && Boolean(check) && Boolean(provider?.configured)
    && sendPending.length === 1 && sendPending[0] === 'profile';
  const useTestMode = showTestSwitch && testMode;

  const missingList = (missing) => (missing || []).map((m) => m.label).join(', ');
  const operationList = (operations) => (operations || []).map((op) => t(k(`operations.${op}`))).join(', ');

  // Izydocs sin configurar o con alguna de las operaciones pendiente (null = disponible)
  const providerReason = (...operations) => {
    if (provider && !provider.configured) return t(k('reasons.notConfigured'));
    if (operations.some((op) => provider?.pending?.includes(op))) {
      return t(k('reasons.notWired'), { list: operationList(provider.pending) });
    }
    return null;
  };

  let sendReason = null;
  if (!check) sendReason = t(k('reasons.checkError'));
  // Enviar enrola a la persona y necesita los datos de su perfil en Izydocs
  else if (providerReason(...(useTestMode ? ['enrollment'] : ['enrollment', 'profile']))) {
    sendReason = providerReason(...(useTestMode ? ['enrollment'] : ['enrollment', 'profile']));
  }
  else if (check.reason === 'FORMAT_NOT_AVAILABLE') sendReason = t(k('reasons.formatNotAvailable'));
  else if (check.reason === 'STALE_SIMULATION') sendReason = t(k('reasons.stale'));
  else if (check.reason === 'GRATIFICATION_NOT_SUPPORTED') sendReason = t(k('reasons.gratification'));
  else if (check.reason === 'DATA_MISSING') sendReason = t(k('reasons.missing'), { list: missingList(check.missing) });
  const refreshReason = inProgress ? t(k('inProgressHint')) : providerReason('status');
  const cancelReason = inProgress ? t(k('inProgressHint')) : providerReason('cancellation');

  // Datos del contrato que cambiaron desde el envío (el de Izydocs tiene los anteriores)
  const changedLabels = signature?.tags && ['sent', 'signed_by_worker', 'completed'].includes(status)
    ? (check?.tags || [])
      .filter((tag) => !IGNORED_CHANGES.includes(tag.tag) && tag.tag in signature.tags && signature.tags[tag.tag] !== tag.value)
      .map((tag) => tag.label)
    : [];

  const showError = (data) => {
    const code = data?.code;
    if (code === 'IZYDOCS_NOT_CONFIGURED') showAlert(t(k('reasons.notConfigured')), 'warning');
    else if (code === 'IZYDOCS_NOT_WIRED') {
      showAlert(t(k('reasons.notWired'), { list: operationList(data.pending || provider?.pending) }), 'warning');
    } else if (code === 'SIGNATURE_DATA_MISSING') showAlert(t(k('reasons.missing'), { list: missingList(data.missing) }), 'warning');
    else if (code === 'FORMAT_NOT_AVAILABLE') showAlert(t(k('reasons.formatNotAvailable')), 'warning');
    else if (code === 'STALE_SIMULATION') showAlert(t(k('reasons.stale')), 'warning');
    else if (code === 'GRATIFICATION_NOT_SUPPORTED') showAlert(t(k('reasons.gratification')), 'warning');
    else if (code === 'SIGNATURE_ALREADY_SENT') showAlert(t(k('errors.alreadySent')), 'warning');
    else if (code === 'SIGNATURE_NOT_OPEN') showAlert(t(k('errors.notOpen')), 'warning');
    else if (code === 'SIGNATURE_IN_PROGRESS') showAlert(t(k('errors.inProgress')), 'warning');
    else if (code === 'SIGNATURE_CHANGED') showAlert(t(k('errors.changed')), 'warning');
    else if (code === 'IZYDOCS_UNCERTAIN') showAlert(t(k('errors.uncertain')), 'warning');
    else if (code === 'IZYDOCS_LOAD_NOT_FOUND') showAlert(t(k('errors.loadNotFound')), 'warning');
    else if (code === 'SIGNATURE_TEST_MODE_FORBIDDEN') showAlert(t(k('errors.testModeForbidden')), 'warning');
    else if (code === 'IZYDOCS_ERROR') {
      showAlert(t(k('errors.izydocs'), { message: [data.message, data.detail].filter(Boolean).join(' · ') }), 'error');
    } else if (code === 'SIGNED_FILE_NOT_AVAILABLE') showAlert(t(k('errors.fileNotAvailable')), 'warning');
    else if (code === 'SIGNED_FILE_NOT_SAVED') showAlert(t(k('errors.fileNotSaved')), 'error');
    else showAlert(t(k('errors.generic')), 'error');
  };

  // Ejecuta la acción y recarga el detalle (también si falló: el envío puede haber quedado en failed)
  const run = async (request, successMessage) => {
    try {
      setWorking(true);
      const response = await request();
      showAlert(successMessage(response.data?.response?.data), 'success');
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.response);
    } finally {
      setWorking(false);
      onChanged?.();
    }
  };

  const handleSend = async () => {
    const asTest = useTestMode;
    await run(
      () => api.post(`/erp/contracts/${contract.uid}/signature`,
        { organizationId: organization, ...(asTest ? { testMode: true } : {}) }, { headers }),
      () => t(k(asTest ? 'testMode.sent' : 'sent'))
    );
    setSendOpen(false);
    setTestMode(false); // cada envío de prueba se activa de nuevo
  };

  const handleRefresh = () =>
    run(
      () => api.post(`/erp/contracts/${contract.uid}/signature/refresh`, { organizationId: organization }, { headers }),
      (updated) => t(k('refreshed'), { status: t(k(`statuses.${updated?.signature?.status || status}`)) })
    );

  const handleCancel = async () => {
    await run(
      () => api.delete(`/erp/contracts/${contract.uid}/signature`, { params: { organizationId: organization }, headers }),
      () => t(k('cancelled'))
    );
    setCancelOpen(false);
  };

  const handleDownload = async () => {
    try {
      setWorking(true);
      const response = await api.get(`/erp/contracts/${contract.uid}/signature/document`, {
        params: { organizationId: organization },
        headers,
        responseType: 'blob',
      });
      saveBlob(response.data, `contrato_firmado_${contract.workerRut || contract.uid}.pdf`, 'application/pdf');
    } catch (err) {
      console.error(err);
      showError(await blobErrorBody(err));
    } finally {
      setWorking(false);
    }
  };

  const details = signature
    ? [
        t(k('internalDocument'), { number: signature.internalDocument }),
        t(k('sentAt'), { date: formatDateTime(signature.sentAt) }),
        signature.lastCheckedAt ? t(k('lastCheckedAt'), { date: formatDateTime(signature.lastCheckedAt) }) : null,
        signature.providerStatusText ? t(k('providerStatus'), { status: signature.providerStatusText }) : null,
        signature.completedAt ? t(k('completedAt'), { date: formatDateTime(signature.completedAt) }) : null,
        signature.cancelledAt ? t(k('cancelledAt'), { date: formatDateTime(signature.cancelledAt) }) : null,
      ].filter(Boolean).join(' · ')
    : '';

  if (!isSuperAdmin && !released) return null;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>{t(k('title'))}</Typography>
          {status && <Chip size="small" color={STATUS_COLORS[status] || 'default'} label={t(k(`statuses.${status}`))} />}
          {signature?.testMode && <Chip size="small" variant="outlined" color="warning" label={t(k('testMode.chip'))} />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canSend && (
            <Tooltip title={sendReason || t(k('sendHint'))}>
              <span>
                <Button size="small" variant="contained" startIcon={<SendIcon />}
                  disabled={working || Boolean(sendReason)} onClick={() => setSendOpen(true)} sx={{ textTransform: 'none' }}>
                  {t(k('send'))}
                </Button>
              </span>
            </Tooltip>
          )}
          {canRefresh && (
            <Tooltip title={refreshReason || ''}>
              <span>
                <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
                  disabled={working || Boolean(refreshReason)} onClick={handleRefresh} sx={{ textTransform: 'none' }}>
                  {t(k('refresh'))}
                </Button>
              </span>
            </Tooltip>
          )}
          {canCancel && (
            <Tooltip title={cancelReason || ''}>
              <span>
                <Button size="small" color="error" startIcon={<CancelIcon />}
                  disabled={working || Boolean(cancelReason)} onClick={() => setCancelOpen(true)} sx={{ textTransform: 'none' }}>
                  {t(k('cancel'))}
                </Button>
              </span>
            </Tooltip>
          )}
          {signature?.signedFile && (
            <Button size="small" variant="contained" startIcon={<PdfIcon />}
              disabled={working} onClick={handleDownload} sx={{ textTransform: 'none' }}>
              {t(k('download'))}
            </Button>
          )}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />

      {signature ? (
        <>
          <Typography variant="body2">{t(k('format'), { id: signature.documentId, name: signature.formatName })}</Typography>
          <Typography variant="body2" color="text.secondary">{details}</Typography>
          {status === 'failed' && signature.error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {signature.error.code === 'IZYDOCS_LOAD_NOT_FOUND'
                ? t(k('failedNotFound'))
                : t(k('failed'), { message: [signature.error.message, signature.error.detail].filter(Boolean).join(' · ') })}
            </Alert>
          )}
          {status === 'sending' && <Alert severity="info" sx={{ mt: 1 }}>{t(k('sendingHint'))}</Alert>}
          {changedLabels.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t(k(status === 'completed' ? 'dataChangedSigned' : 'dataChanged'), { list: changedLabels.join(', ') })}
            </Alert>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {check?.format ? `${t(k('notSent'))} ${t(k('format'), { id: check.format.documentId, name: check.format.name })}` : t(k('notSent'))}
        </Typography>
      )}
      {canSend && sendReason && (
        <Alert severity={check ? 'warning' : 'error'} sx={{ mt: 1 }}>{sendReason}</Alert>
      )}
      {showTestSwitch && (
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={<Switch size="small" checked={testMode} disabled={working} onChange={(e) => setTestMode(e.target.checked)} />}
            label={t(k('testMode.label'))}
          />
          <Typography variant="caption" color="text.secondary" display="block">{t(k('testMode.hint'))}</Typography>
        </Box>
      )}

      {/* Confirmar el envío: formato y valores que se mandan a Izydocs */}
      <Dialog open={sendOpen} onClose={() => !working && setSendOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t(k('dialog.title'))}</DialogTitle>
        <DialogContent>
          {useTestMode && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {t(k('testMode.dialogWarning'), { name: contract.workerName || formatRut(contract.workerRut) })}
            </Alert>
          )}
          {check?.format && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t(k('dialog.intro'), { id: check.format.documentId, name: check.format.name })}
            </Typography>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t(k('dialog.tag'))}</TableCell>
                <TableCell>{t(k('dialog.value'))}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(check?.tags || []).map((tag) => (
                <TableRow key={tag.tag}>
                  <TableCell>{tag.label}</TableCell>
                  <TableCell>{tag.value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t(k('dialog.person'), {
              name: contract.workerName || '—',
              rut: formatRut(contract.workerRut),
              email: employeeRecord?.personal?.email || '—',
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">{t(k('dialog.personNote'))}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={working}>{t(k('dialog.cancel'))}</Button>
          <Button variant="contained" onClick={handleSend} disabled={working}>{t(k('dialog.confirm'))}</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar la anulación */}
      <Dialog open={cancelOpen} onClose={() => !working && setCancelOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t(k('cancelDialog.title'))}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t(k('cancelDialog.body'))}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)} disabled={working}>{t(k('cancelDialog.back'))}</Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={working}>{t(k('cancelDialog.confirm'))}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ErpContractSignatureCard;
